import { NodeViewContextableWrapper } from "@ext/markdown/core/element/NodeViewContextableWrapper";
import { useBlockToolbar } from "@ext/markdown/elements/article/edit/helpers/useBlockToolbar";
import BlockCommentView from "@ext/markdown/elements/comment/edit/components/View/BlockCommentView";
import FragmentActions from "@ext/markdown/elements/fragment/edit/components/FragmentActions";
import FragmentUpdateService, {
	type ShownFragment,
} from "@ext/markdown/elements/fragment/edit/components/FragmentUpdateService";
import FragmentService from "@ext/markdown/elements/fragment/edit/components/Tab/FragmentService";
import getFragmentScope from "@ext/markdown/elements/fragment/logic/getFragmentScope";
import Fragment from "@ext/markdown/elements/fragment/render/components/Fragment";
import type { NodeViewProps } from "@tiptap/react";
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";

/** The service's last update, until the article is read back: the block is updated in place, not mounted again. */
const useShownFragment = (node: NodeViewProps["node"]) => {
	const parsed: ShownFragment = { content: node.attrs.content, error: node.attrs.error };
	const [update, setUpdate] = useState<ShownFragment | null>(null);
	const [updatedOver, setUpdatedOver] = useState(parsed);
	if (updatedOver.content !== parsed.content || updatedOver.error !== parsed.error) {
		setUpdatedOver(parsed);
		setUpdate(null);
	}
	return { shown: update ?? parsed, show: setUpdate };
};

const FragmentComponent = (props: NodeViewProps): ReactElement => {
	const { node } = props;
	const { shown, show } = useShownFragment(node);
	const hoverElementRef = useRef<HTMLDivElement>(null);
	const isEditable = useMemo(
		() => node.attrs.id && node.attrs.title && !node.attrs.foreign,
		[node.attrs.id, node.attrs.title, node.attrs.foreign],
	);

	useEffect(() => {
		const scope = getFragmentScope(node.attrs.type);
		FragmentUpdateService.addUpdateContent(node.attrs.id, show, scope);
		return () => FragmentUpdateService.removeUpdateContent(node.attrs.id, show, scope);
	}, [node.attrs.id, node.attrs.type, show]);

	const handleEdit = useCallback(() => {
		FragmentService.openItem({ id: node.attrs.id, title: node.attrs.title });
	}, [node.attrs.id, node.attrs.title]);

	useBlockToolbar(props, isEditable ? <FragmentActions onClickEdit={handleEdit} /> : null, { comment: true });

	return (
		<NodeViewContextableWrapper data-drag-handle draggable={true} props={props} ref={hoverElementRef}>
			<>
				<BlockCommentView className="-mx-2 -mt-1 mb-[0.2em] px-2 pt-1 pb-0" commentId={node.attrs.comment?.id}>
					<Fragment
						content={shown.content}
						error={shown.error}
						id={node.attrs.id}
						owner={node.attrs.owner}
						type={node.attrs.type}
					/>
				</BlockCommentView>
			</>
		</NodeViewContextableWrapper>
	);
};

export default FragmentComponent;
